"use client";

import type { FC } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import { useConfig } from "@jti/app-context/useConfig";
import { DataLayerComponent } from "@jti/loyalty/components/DataLayer";
import { Layout } from "@jti/loyalty/components/Layout";
import { PageRunner } from "@jti/loyalty/components/PageRunner";
import { LoyaltyWrapperProvider } from "@jti/loyalty/utils";
import { isRestrictedSlug } from "@jti/loyalty/utils/isRestrictedSlug";

import HiddenPageRedirectionLayer from "@/context/PageConfigContext/hidden-page-redirection-layer/HiddenPageRedirectionLayer";

import { Placeholder } from "../Placeholder/Placeholder";

import type { loyaltyWrapperInputs } from "./LoyaltyWrapper.builder";

export type LoyaltyWrapperBuilderProps = BuilderComponentProps<typeof loyaltyWrapperInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

const REDIRECT_TIME = 3000;

export const LoyaltyWrapper: FC<LoyaltyWrapperBuilderProps> = ({
  name,
  hideMyAddress,
  builderBlock,
  builderContext,
  builderComponents,
}) => {
  const isInEditor = isEditing();

  const {
    loyaltyRestrictionPurpose = [],
    loyaltyRestrictionWithoutRedirectionSlugUrls,
    isLoyaltyContentRestricted,
    isAgeVerifiedLoyaltyRestriction,
    isDeviceRegisteredRestriction,
  } = useConfig();

  const isSlugWithoutRedirection = isRestrictedSlug(loyaltyRestrictionWithoutRedirectionSlugUrls);
  const shouldCheckConsents =
    isLoyaltyContentRestricted && loyaltyRestrictionPurpose.length > 0 && !isSlugWithoutRedirection && !isInEditor;

  const requiredConsentPurposes = loyaltyRestrictionPurpose.map((id: string) => ({ purpose: id }));

  return (
    <LoyaltyWrapperProvider>
      <DataLayerComponent>
        <PageRunner name={name} isInEditor={isInEditor} />
        {shouldCheckConsents && (
          <HiddenPageRedirectionLayer
            userShouldBeAgeVerified={!!isAgeVerifiedLoyaltyRestriction}
            userShouldDeviceBeRegistered={!!isDeviceRegisteredRestriction}
            redirectPageForNotAllowedUsers=""
            redirectToAuthPage={false}
            requiredConsentPurposes={requiredConsentPurposes}
            redirectTime={REDIRECT_TIME}
            isInEditor={isInEditor}
          />
        )}
        <Layout name={name} isInEditor={isInEditor} hideMyAddress={hideMyAddress}>
          {builderBlock?.children?.map((child) => (
            <Blocks
              key={child.id}
              blocks={[child]}
              parent={builderBlock?.id}
              path="children"
              context={builderContext}
              registeredComponents={builderComponents}
            />
          ))}
          {isInEditor && <Placeholder>Drag and drop component here</Placeholder>}
        </Layout>
      </DataLayerComponent>
    </LoyaltyWrapperProvider>
  );
};
